/**
 * Printed date → the pipeline's `yyyy-mm-dd` contract.
 *
 * CLASSIFY asks the model for the letting/completion date EXACTLY as printed, so
 * converting the prose is entirely this module's job. Bid tabs print a date
 * wrapped in whatever the header says around it — a time before it ("10:00 a.m.
 * Wednesday, October 22, 2025"), a time after it ("June 3, 2026 at 2:00 PM"), a
 * weekday, an ordinal day, `a.m.`/`p.m.` with periods.
 *
 * `new Date(str)` cannot be the parser here. Its legacy fallback rejects a time
 * token before the date and the bare word `at` (both of the forms above yield
 * Invalid Date), accepts things it should refuse by inventing the missing parts
 * ("2025" → January 1), and resolves a bare-year/ISO string in UTC, which shifts
 * the day west of Greenwich. So this scans for a date SHAPE and builds the ISO
 * string from the captured numbers — no Date parsing, no timezone, no locale.
 *
 * The rule is: recognize the ordinary printed forms, and return null for
 * anything else. A month with no day ("October 2025") is NOT a date — inventing
 * the 1st is a guess, and a guessed letting date is worse than a blank one.
 */

/** Month names + the abbreviations bid tabs actually print. Full names first so
 *  the alternation never settles for a prefix of a longer name. */
const MONTHS: ReadonlyArray<readonly [string, number]> = [
  ['january', 1],
  ['february', 2],
  ['march', 3],
  ['april', 4],
  ['may', 5],
  ['june', 6],
  ['july', 7],
  ['august', 8],
  ['september', 9],
  ['october', 10],
  ['november', 11],
  ['december', 12],
  ['sept', 9],
  ['jan', 1],
  ['feb', 2],
  ['mar', 3],
  ['apr', 4],
  ['jun', 6],
  ['jul', 7],
  ['aug', 8],
  ['sep', 9],
  ['oct', 10],
  ['nov', 11],
  ['dec', 12],
];

const MONTH_NUMBER = new Map(MONTHS);
const MONTH_NAME = MONTHS.map(([name]) => name).join('|');
const ORDINAL = '(?:st|nd|rd|th)?';

/** "October 22, 2025", "Oct. 22 2025", "January 20th, 2026". */
const MONTH_DAY_YEAR = new RegExp(
  `\\b(${MONTH_NAME})\\.?\\s+(\\d{1,2})${ORDINAL}\\s*,?\\s*(\\d{4})(?!\\d)`,
  'gi',
);

/** "22 October 2025", "3rd of June 2026". */
const DAY_MONTH_YEAR = new RegExp(
  `(?<!\\d)(\\d{1,2})${ORDINAL}\\s+(?:of\\s+)?(${MONTH_NAME})\\.?,?\\s+(\\d{4})(?!\\d)`,
  'gi',
);

/** "Oct-22-2025", "October-22nd-2025". */
const MONTH_DAY_YEAR_DASH = new RegExp(
  `\\b(${MONTH_NAME})\\.?-(\\d{1,2})${ORDINAL}-(\\d{4})(?!\\d)`,
  'gi',
);

/** "22-Oct-2025", "22nd-October-2025". */
const DAY_MONTH_YEAR_DASH = new RegExp(
  `(?<!\\d)(\\d{1,2})${ORDINAL}-(${MONTH_NAME})\\.?-(\\d{4})(?!\\d)`,
  'gi',
);

/** "2025-10-22" (and any ISO timestamp that starts with one), "2025/10/22". */
const YEAR_MONTH_DAY = /(?<!\d)(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?!\d)/g;

/** "4/22/2026", "10-18-2023", "4/22/26" — US month-first, the only numeric
 *  order a US bid tab prints. A day-first string ("18/10/2023") reads as month
 *  18, fails validation, and yields null rather than a silently swapped date. */
const MONTH_DAY_YEAR_NUMERIC =
  /(?<!\d)(\d{1,2})[/-](\d{1,2})[/-](\d{2}(?:\d{2})?)(?!\d)/g;

/** Letting Date also accepts dots, but never chooses a century for the user. */
const LETTING_MONTH_DAY_YEAR_NUMERIC =
  /(?<!\d)(\d{1,2})[./-](\d{1,2})[./-](\d{4})(?!\d)/g;

/** Preserve the existing policy for date fields other than Letting Date. */
function fullYear(printed: string): number {
  const n = Number(printed);
  if (printed.length === 4) {
    return n;
  }
  return n <= 49 ? 2000 + n : 1900 + n;
}

/** The last day of a month, leap years included. Date is used for ARITHMETIC
 *  only (UTC, no parsing), so no local timezone can shift it. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

interface DateHit {
  index: number;
  year: number;
  month: number;
  day: number;
}

function hitsOf(
  text: string,
  pattern: RegExp,
  read: (m: RegExpExecArray) => Omit<DateHit, 'index'>,
): DateHit[] {
  const hits: DateHit[] = [];
  const scanner = new RegExp(pattern.source, pattern.flags);
  let match = scanner.exec(text);
  while (match) {
    hits.push({ index: match.index, ...read(match) });
    match = scanner.exec(text);
  }
  return hits;
}

function monthOf(name: string | undefined): number {
  return MONTH_NUMBER.get((name ?? '').toLowerCase()) ?? 0;
}

/**
 * The printed date normalized to `yyyy-mm-dd`, or null when the text names no
 * unambiguous day. Pure, timezone-free and idempotent — an already-normalized
 * `yyyy-mm-dd` comes back unchanged.
 */
function normalizedDayOf(value: unknown, lettingDate: boolean): string | null {
  const text =
    typeof value === 'string'
      ? value.trim()
      : value == null
        ? ''
        : String(value).trim();
  if (text.length === 0) {
    return null;
  }

  const hits = [
    ...hitsOf(text, MONTH_DAY_YEAR, (m) => ({
      year: fullYear(m[3] ?? ''),
      month: monthOf(m[1]),
      day: Number(m[2]),
    })),
    ...hitsOf(text, DAY_MONTH_YEAR, (m) => ({
      year: fullYear(m[3] ?? ''),
      month: monthOf(m[2]),
      day: Number(m[1]),
    })),
    ...(lettingDate
      ? hitsOf(text, MONTH_DAY_YEAR_DASH, (m) => ({
          year: Number(m[3]),
          month: monthOf(m[1]),
          day: Number(m[2]),
        }))
      : []),
    ...(lettingDate
      ? hitsOf(text, DAY_MONTH_YEAR_DASH, (m) => ({
          year: Number(m[3]),
          month: monthOf(m[2]),
          day: Number(m[1]),
        }))
      : []),
    ...hitsOf(text, YEAR_MONTH_DAY, (m) => ({
      year: fullYear(m[1] ?? ''),
      month: Number(m[2]),
      day: Number(m[3]),
    })),
    ...hitsOf(
      text,
      lettingDate ? LETTING_MONTH_DAY_YEAR_NUMERIC : MONTH_DAY_YEAR_NUMERIC,
      (m) => ({
        year: fullYear(m[3] ?? ''),
        month: Number(m[1]),
        day: Number(m[2]),
      }),
    ),
  ]
    .filter(
      (h) =>
        h.month >= 1 &&
        h.month <= 12 &&
        h.day >= 1 &&
        h.day <= daysInMonth(h.year, h.month),
    )
    .sort((a, b) => a.index - b.index);

  // Invalid date-shaped identifiers do not count, but two different valid days
  // are ambiguous. Repeating the same day in one header is still unambiguous.
  const distinctDays = new Set(
    hits.map(({ year, month, day }) => `${year}-${month}-${day}`),
  );
  if (distinctDays.size !== 1) {
    return null;
  }
  const hit = hits[0];
  if (!hit) {
    return null;
  }
  return `${String(hit.year).padStart(4, '0')}-${String(hit.month).padStart(
    2,
    '0',
  )}-${String(hit.day).padStart(2, '0')}`;
}

export function isoDayOf(value: unknown): string | null {
  return normalizedDayOf(value, false);
}

/** Letting Date uses four-digit years and its additional printed formats. */
export function isoLettingDayOf(value: unknown): string | null {
  return normalizedDayOf(value, true);
}
